import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Settings() {
  const { user, orgs, refresh } = useAuth();
  const activeOrg = orgs.find((o) => o.id === user?.active_org_id);
  const [form, setForm] = useState({ name: "", address: "", email: "", phone: "", responsible_person: "", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const canEdit = user?.platform_role === "super_admin" || user?.active_role === "admin_mitra";

  useEffect(() => {
    if (activeOrg) setForm({
      name: activeOrg.name || "", address: activeOrg.address || "", email: activeOrg.email || "",
      phone: activeOrg.phone || "", responsible_person: activeOrg.responsible_person || "",
      logo_url: activeOrg.logo_url || "",
    });
  }, [activeOrg?.id]);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.patch(`/organizations/${activeOrg.id}`, form);
      toast.success("Profil perusahaan diperbarui");
      await refresh();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  if (!activeOrg) return <div className="gc-card">Belum ada organisasi aktif.</div>;

  return (
    <div className="space-y-6">
      <div>
        <span className="gc-label">Pengaturan</span>
        <h1 className="gc-h1 mt-1">Profil Perusahaan Pengguna</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-2xl">Perbarui identitas perusahaan pengguna yang akan ditampilkan pada laporan sebagai <em>Organisasi Pengguna</em>. Nama sistem, tagline, dan pengembang (Politeknik Negeri Manado) tidak dapat diubah dari sini.</p>
      </div>

      <form onSubmit={submit} className="gc-card grid md:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <label className="gc-label block mb-1.5">Nama Perusahaan</label>
          <input required disabled={!canEdit} className="gc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Penanggung Jawab</label>
          <input disabled={!canEdit} className="gc-input" value={form.responsible_person} onChange={(e) => setForm({ ...form, responsible_person: e.target.value })} />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Email</label>
          <input disabled={!canEdit} type="email" className="gc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Telepon</label>
          <input disabled={!canEdit} className="gc-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="gc-label block mb-1.5">Alamat</label>
          <textarea disabled={!canEdit} rows={2} className="gc-textarea" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="gc-label block mb-1.5">URL Logo</label>
          <input disabled={!canEdit} className="gc-input" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
          <p className="text-[11px] text-slate-400 mt-1">Logo akan ditampilkan pada laporan dan navigasi (opsional).</p>
        </div>
        {canEdit && (
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={saving} className="gc-btn-primary" data-testid="btn-save-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4" /> Simpan</>)}</button>
          </div>
        )}
      </form>

      <div className="gc-card max-w-3xl">
        <span className="gc-label">Identitas Sistem</span>
        <div className="mt-2 text-sm text-slate-600 space-y-1.5">
          <div className="flex justify-between border-b border-slate-100 pb-2"><span>Nama Sistem</span><span className="font-semibold text-slate-900">GREENCON HPP</span></div>
          <div className="flex justify-between border-b border-slate-100 pb-2"><span>Deskripsi</span><span className="font-semibold text-slate-900 text-right">Sistem Informasi Akuntansi HPP Beton Rendah Karbon</span></div>
          <div className="flex justify-between border-b border-slate-100 pb-2"><span>Pengembang / Pemilik Sistem</span><span className="font-semibold text-emerald-700">Politeknik Negeri Manado</span></div>
          <div className="flex justify-between"><span>Peran Perusahaan Pengguna</span><span className="font-semibold text-slate-900">Pengguna sistem (data operasional)</span></div>
        </div>
      </div>
    </div>
  );
}
